import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { UseFormSetValue, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useProductCenterApi } from 'api';
import { brandsQuery, modelsQuery } from 'api/queries/InstallationQueries';
import { IdLabelDto } from 'api/responses/models/Common/IdLabelDto';
import { convertWeightToTonnes } from 'api/responses/models/Unit/UnitMeasurement';
import { MainSegment } from 'api/responses/models/Unit/UnitStructure/MainSegment';
import {
	customBrandKey,
	customModelKey,
	isCustomBrand,
	isCustomModel,
} from 'domain/unit/brandAndModel';
import { ControlledAutocomplete } from 'library/components/controls/form/ControlledAutocomplete';
import { ControlledTextField } from 'library/components/controls/form/ControlledTextField';
import { UnitDataFormTypes, isUnitMarine } from 'modules/Shared/State/unit';

import { brandFieldValidation, modelFieldValidation } from '../Validations';

const getColumnSpan = (isShort: boolean) =>
	isShort
		? { xs: 'span 16', sm: 'span 8', md: 'span 3' }
		: { xs: 'span 16', sm: 'span 16', md: 'span 6' };

const shouldBeDisplayed = (display: boolean) => (display ? undefined : 'none');

type BrandModelSectionProps = {
	mainSegment: MainSegment | null;
};
export const BrandModelSection = ({ mainSegment }: BrandModelSectionProps) => {
	const {
		control,
		formState: {
			dirtyFields: { brand: isBrandDirty, model: isModelDirty },
		},
		resetField,
		setValue,
	} = useFormContext<UnitDataFormTypes>();

	const { t } = useTranslation(['unitCreation', 'commonUnit']);
	const { enqueueSnackbar } = useSnackbar();

	const brandValue = useWatch({ control, name: 'brand' });
	const modelValue = useWatch({ control, name: 'model' });
	const customBrandValue = useWatch({ control, name: 'customBrand' });
	const customModelValue = useWatch({ control, name: 'customModel' });

	const initialBrand = useRef(brandValue);
	const initialModel = useRef(modelValue);

	const isCustomBrandVal = useMemo(
		() => isCustomBrand(brandValue?.id),
		[brandValue?.id]
	);
	const isCustomModelVal = useMemo(
		() => isCustomModel(modelValue?.id),
		[modelValue?.id]
	);

	const customModelOption: IdLabelDto = useMemo(
		() => ({
			id: customModelKey,
			label: t('unitCreation:unit-information-custom-model-option-label'),
		}),
		[t]
	);

	const customBrandOption: IdLabelDto = useMemo(
		() => ({
			id: customBrandKey,
			label: t('unitCreation:unit-information-custom-brand-option-label'),
		}),
		[t]
	);

	const { api } = useProductCenterApi();
	const { data: brandsData, isLoading: brandsIsLoading } = useQuery({
		...brandsQuery(api, mainSegment as MainSegment),
		enabled: mainSegment !== null,
	});

	const { data: modelsData, isLoading: modelsIsLoading } = useQuery({
		...modelsQuery(api, brandValue ? parseInt(brandValue.id) : 0),
		enabled: brandValue?.id !== null && !isCustomBrandVal,
	});

	// setup correct brand based on label name
	useEffect(() => {
		if (!brandsIsLoading && brandsData) {
			const brand = brandsData.find(
				({ label }) => label === customBrandValue
			);

			if (brand) {
				initialBrand.current = brand;
				setValue('brand', brand);
			}
		}
	}, [brandsData, brandsIsLoading, customBrandValue, setValue]);

	// setup correct model based on label name
	useEffect(() => {
		if (initialBrand && !modelsIsLoading && modelsData) {
			const model = modelsData.find(
				({ label }) => label === customModelValue
			);

			if (model) {
				setValue('model', model);
			}
		}
	}, [
		control,
		customModelValue,
		initialBrand,
		modelsData,
		modelsIsLoading,
		setValue,
	]);

	useEffect(() => {
		if (
			initialBrand.current?.label !== customBrandValue &&
			initialBrand.current?.id !== brandValue?.id
		) {
			resetField('model', { defaultValue: null });

			if (
				isUnitMarine(mainSegment) &&
				brandValue &&
				!modelsData &&
				!modelsIsLoading
			) {
				resetAllDimensions();
				enqueueSnackbar(t('commonUnit:unavailable-dimensions'), {
					variant: 'warning',
				});
			}
		}
	}, [brandValue?.id]);

	const resetLength = useCallback(() => {
		resetField('length', { defaultValue: '' });
		resetField('lengthUnit', { defaultValue: '' });
	}, [resetField]);

	const resetWidth = useCallback(() => {
		resetField('width', { defaultValue: '' });
		resetField('widthUnit', { defaultValue: '' });
	}, [resetField]);

	const resetWeight = useCallback(() => {
		resetField('displacement', { defaultValue: '' });
		resetField('displacementUnit', { defaultValue: '' });
	}, [resetField]);

	const resetSizeDimensions = useCallback(() => {
		resetLength();
		resetWidth();
	}, [resetLength, resetWidth]);

	const resetAllDimensions = useCallback(() => {
		resetSizeDimensions();
		resetWeight();
	}, [resetSizeDimensions, resetWeight]);

	useEffect(() => {
		if (!isUnitMarine(mainSegment) || (!isBrandDirty && !isModelDirty)) {
			return;
		}

		if (
			!modelsData ||
			!modelValue ||
			!modelsData.find(({ id }) => modelValue.id === id)
		) {
			resetAllDimensions();
			return;
		}

		if (
			initialModel.current?.id !== modelValue?.id &&
			!isCustomBrand(modelValue?.id)
		) {
			resetField('customModel', { defaultValue: null });
		}

		const model = modelsData.find(({ id }) => modelValue?.id === id);

		if (model?.dimension) {
			const {
				dimension: { length, weight, width, unitOfSize, unitOfWeight },
			} = model;

			setValue('dimensionsType', 'Actual');
			if (unitOfSize) {
				setOrResetLength(length, setValue, unitOfSize, resetLength);
				setOrResetWidth(width, setValue, unitOfSize, resetWidth);
			} else {
				resetSizeDimensions();
			}

			setOrResetWeight(weight, unitOfWeight, setValue, resetWeight);
		} else {
			resetAllDimensions();
			enqueueSnackbar(t('commonUnit:unavailable-dimensions'), {
				variant: 'warning',
			});
		}
	}, [modelValue, modelsData]);

	return (
		<Box
			component={Paper}
			variant="flatGrey"
			sx={{
				display: 'grid',
				gridTemplateColumns: 'repeat(16, 1fr)',
				gap: 2,
				p: 2,
			}}>
			<Typography
				variant="h3"
				sx={{
					gridColumn: { xs: 'span 16', md: 'span 4' },
				}}>
				{t('unitCreation:unit-information-brand-model-title')}
			</Typography>
			<ControlledAutocomplete
				id="brand-select"
				name="brand"
				control={control}
				label={t('commonUnit:brand')}
				loading={brandsIsLoading}
				options={[...(brandsData || []), customBrandOption]}
				size="small"
				sx={{ gridColumn: getColumnSpan(isCustomBrandVal) }}
				validation={brandFieldValidation}
				getOptionId={(option: any) => option}
			/>
			{shouldBeDisplayed(!isCustomBrandVal) ? (
				<ControlledTextField
					id="custom-brand-field"
					name="customBrand"
					label={t(
						'unitCreation:unit-information-custom-brand-input'
					)}
					control={control}
					size="small"
					fullWidth
					sx={{
						gridColumn: {
							xs: 'span 16',
							sm: 'span 8',
							md: 'span 3',
						},
					}}
					validation={brandFieldValidation}
				/>
			) : (
				<></>
			)}
			<ControlledAutocomplete
				id="model-select"
				name="model"
				control={control}
				label={t('commonUnit:model')}
				loading={modelsIsLoading}
				options={[...(modelsData || []), customModelOption]}
				size="small"
				sx={{ gridColumn: getColumnSpan(isCustomModelVal) }}
				validation={modelFieldValidation}
				getOptionId={(option: any) => option}
			/>
			{shouldBeDisplayed(!isCustomModelVal) ? (
				<ControlledTextField
					id="custom-model-field"
					name="customModel"
					label={t(
						'unitCreation:unit-information-custom-model-input'
					)}
					control={control}
					size="small"
					fullWidth
					sx={{
						gridColumn: {
							xs: 'span 16',
							sm: 'span 8',
							md: 'span 3',
						},
					}}
					validation={modelFieldValidation}
				/>
			) : (
				<></>
			)}
		</Box>
	);
};

const setOrResetWeight = (
	weight: number | null,
	unitOfWeight: string | null,
	setValue: UseFormSetValue<UnitDataFormTypes>,
	resetWeight: () => void
) => {
	const { weight: convertedWeight, unitOfWeight: convertedUnitOfWeight } =
		convertWeightToTonnes(weight, unitOfWeight);

	if (convertedWeight && convertedUnitOfWeight) {
		setValue('displacement', convertedWeight.toString());
		setValue('displacementUnit', convertedUnitOfWeight);
	} else {
		resetWeight();
	}
};

const setOrResetWidth = (
	width: number | null,
	setValue: UseFormSetValue<UnitDataFormTypes>,
	unitOfSize: string,
	resetWidth: () => void
) => {
	if (width) {
		setValue('width', width.toString());
		setValue('widthUnit', unitOfSize);
	} else {
		resetWidth();
	}
};

const setOrResetLength = (
	length: number | null,
	setValue: UseFormSetValue<UnitDataFormTypes>,
	unitOfSize: string,
	resetLength: () => void
) => {
	if (length) {
		setValue('length', length.toString());
		setValue('lengthUnit', unitOfSize);
	} else {
		resetLength();
	}
};
